import { Column, Icon, isIconName, Row, StackingContext, Text, usePulse } from "@mdotm/shadcn/legacy/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { useAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { instanceEqualsOrFastDeepEqual, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { identity } from "@mdotm/utils/functions";
import { IconButton, MaybeSkeleton } from "@mdotm/shadcn/mdotm/components";
import { useAsync } from "@mdotm/utils/promise";
import { ForEach } from "@mdotm/utils/react-extra";
// Import from `/production`, not the package root: the root entry replaces the panel with a
// no-op unless `process.env.NODE_ENV === "development"`, so in any built environment (rd-dev,
// test, qa, prod) only the `/production` subpath ships the real component. Availability is
// still gated by `useDevtoolsEnabled()` (dev-ish env or ADMIN/ROOT user).
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools/production";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import SPHEREIconWhite from "$root/assets/images/sphere-logo-white.svg";
import env from "$root/env";
import { NotificationCenterSidebarContent } from "$root/functional-areas/notification-center";
import { useNotificationCenterState } from "$root/functional-areas/notification-center/NotificationStore";
import { notificationSettingsRepository } from "$root/functional-areas/notification-settings/repository";
import { useUserValue } from "$root/functional-areas/user";
import { userRepository } from "$root/functional-areas/user/repository";
import { platformToast, ToastContent } from "$root/notification-system/toast";
import { useMappedQueries, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { qualifier } from "$root/utils";
import AuthorizationGuard from "../AuthorizationGuard";
import { Sidebar } from "../common/Sidebar";
import { generateImageObjectUrlFromFileResponse } from "../EvolvedPrint/configuration/utils";
import {
	type AppDrawerModule,
	collapsedDrawerWidth,
	devtoolsPanelHeight$,
	devtoolsPanelMinHeight,
	devtoolsPanelOpen$,
	floatingDrawerWidth,
	mainActiveSidebar$,
	mainSidebarPosition$,
	useAppDrawerState,
	useDevtoolsEnabled,
} from "./AppDrawer-shared";
import { useAppDrawerModules } from "./Modules";
import { useAppVersionCheck } from "./useAppVersionCheck";

/** @internal — exported for tests. The drawer's async/loading/error branches are unreachable through
 * the real modules (none use `onClickAsync`), so they are covered by rendering this directly. */
export function ModuleButton({
	module,
	isActive,
	onHover,
}: Readonly<{
	module: AppDrawerModule;
	isActive: boolean;
	onHover: (m: AppDrawerModule | null) => void;
}>) {
	const history = useHistory();
	const hasAsync = "onClickAsync" in module;
	const [run, asyncState] = useAsync(hasAsync ? (_extra) => module.onClickAsync() : (_extra) => Promise.resolve());
	const errorPulse = usePulse({
		trigger: asyncState.kind === "error",
		duration: 500,
	});
	const isLoading = asyncState.kind === "loading";

	return (
		<button
			data-qualifier={qualifier.appDrawer.item(module.name)}
			className={overrideClassName(
				`inline-flex items-center transition-all outline-hidden rounded p-2 justify-center hover:bg-(--palette-N800) hover:text-(--palette-N0) aria-current:bg-(--palette-N800) aria-current:text-(--palette-N0) ${errorPulse ? "text-(--palette-D500)" : "text-(--palette-N200)"}`,
				{
					rounded: module.background == null,
					"-mx-3": module.background != null,
				},
			)}
			style={{
				background: module.background,
			}}
			aria-current={isActive}
			title={module.title}
			type="button"
			disabled={isLoading}
			data-error-pulse={errorPulse ? "true" : undefined}
			{...("href" in module
				? {
						onClick: () => history.push(module.href),
						onMouseEnter: () => onHover(module),
						onFocus: () => onHover(module),
					}
				: hasAsync
					? // `run` re-throws what the module's `onClickAsync` rejected with, and a DOM click handler
						// discards the promise it returns. `unpromisify` terminates it; the failure is already
						// on screen as the error pulse driven by `asyncState`.
						{ onClick: unpromisify(run) }
					: { onClick: module.onClick })}
		>
			{isLoading ? (
				<Icon icon="calulating" size={20} className="animate-spin ease-in-out" />
			) : typeof module.icon === "string" && isIconName(module.icon) ? (
				<Icon icon={module.icon} size={20} />
			) : (
				module.icon
			)}
		</button>
	);
}

export function AppDrawer() {
	const user = useUserValue();
	const companyQuery = userRepository.customer.useQuery(user.customerId!);
	const { canPin, isPinned, setPin } = useAppDrawerState();
	const togglePin = () => setPin((p) => !p);

	const modules = useAppDrawerModules();

	// Notify once, non-destructively, when a newer version of Sphere has been deployed since this
	// tab was opened. The persistent drawer indicator (see Modules.tsx) stays until the user reloads;
	// this sticky toast is a one-shot nudge to catch their attention.
	const { data: appVersionCheck } = useAppVersionCheck();
	const updateToastShownRef = useRef(false);
	useEffect(() => {
		if (!appVersionCheck?.updateAvailable || updateToastShownRef.current) {
			return;
		}
		updateToastShownRef.current = true;
		platformToast({
			icon: "Reload",
			severity: "info",
			// Reload only via the explicit link-like button — the toast body isn't a click target,
			// so an accidental click can't discard the user's unsaved work.
			children: (
				<ToastContent>
					A new version of Sphere is available.{" "}
					<button type="button" className="underline" onClick={() => window.location.reload()}>
						Reload to update
					</button>
				</ToastContent>
			),
		});
	}, [appVersionCheck?.updateAvailable]);

	const [highlightedModule, _setHighlightedModule] = useState<AppDrawerModule | null>(null);
	const highlightedModuleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const setHighlightedModule = (m: AppDrawerModule | null) => {
		if (!m) {
			highlightedModuleTimeoutRef.current = setTimeout(() => _setHighlightedModule(null), 200);
		} else {
			if (highlightedModuleTimeoutRef.current) {
				clearTimeout(highlightedModuleTimeoutRef.current);
				highlightedModuleTimeoutRef.current = null;
			}
			_setHighlightedModule(m);
		}
	};

	const devtoolsEnabled = useDevtoolsEnabled();
	const [devtoolsPanelOpen, setDevtoolsPanelOpen] = useAtom(devtoolsPanelOpen$);
	const [devtoolsPanelHeight, setDevtoolsPanelHeight] = useAtom(devtoolsPanelHeight$);
	// Vertical resize for the bottom-docked devtools panel. The panel exposes its top edge as a drag
	// handle and delegates the actual resize to this callback (the full ReactQueryDevtools wrapper
	// implements the equivalent); dragging up grows the panel.
	const startDevtoolsResize = (e: React.MouseEvent) => {
		if (e.button !== 0) {
			return;
		}
		const startY = e.clientY;
		const startHeight = devtoolsPanelHeight;
		const onMove = (move: MouseEvent) => {
			move.preventDefault();
			setDevtoolsPanelHeight(Math.max(devtoolsPanelMinHeight, startHeight + (startY - move.clientY)));
		};
		const onUp = () => {
			document.removeEventListener("mousemove", onMove);
			document.removeEventListener("mouseup", onUp);
		};
		document.addEventListener("mousemove", onMove);
		document.addEventListener("mouseup", onUp);
	};

	const { notificationCenterStatus, setNotificationCenterStatus } = useNotificationCenterState();
	const [sidebarPosition, setSidebarPosition] = useAtom(mainSidebarPosition$);
	const [showSidebar, setShowSidebar] = useAtom(mainActiveSidebar$);
	useEffect(() => {
		if (showSidebar === "notifications" && notificationCenterStatus.markAsRead === false) {
			setNotificationCenterStatus({
				...notificationCenterStatus,
				markAsRead: true,
			});
		}
	}, [notificationCenterStatus, showSidebar, setNotificationCenterStatus]);

	const portfolioPoolQuery = notificationSettingsRepository.retrieveAllUserPortfolioNotificationsFromPool.useQuery(0);
	const marketPoolQuery = notificationSettingsRepository.retrieveAllUserMarketNotificationsFromPool.useQuery(0);

	const notificationQueries = useMappedQueries(identity, [], [portfolioPoolQuery, marketPoolQuery]);

	useQueryReactor(notificationQueries, {
		onSuccess: ([portfolioPool, marketPool]) => {
			const areAllRead = [...(marketPool.elements ?? []), ...(portfolioPool.elements ?? [])]?.every((x) => x.read);

			const hasMarketPoolDifferences = marketPool.elements?.some((v, i) => {
				return instanceEqualsOrFastDeepEqual(v, notificationCenterStatus.latestMarketPool[i]) === false;
			});

			const hasPortfolioPoolDifferences = portfolioPool.elements?.some((v, i) => {
				return instanceEqualsOrFastDeepEqual(v, notificationCenterStatus.latestPortfolioPool[i]) === false;
			});

			if (hasPortfolioPoolDifferences === false && hasMarketPoolDifferences === false && areAllRead === false) {
				return;
			}

			if (areAllRead) {
				return setNotificationCenterStatus({
					latestPortfolioPool: portfolioPool.elements ?? [],
					latestMarketPool: marketPool.elements ?? [],
					markAsRead: true,
				});
			}

			setNotificationCenterStatus({
				latestPortfolioPool: hasPortfolioPoolDifferences
					? (portfolioPool.elements ?? [])
					: notificationCenterStatus.latestPortfolioPool,
				latestMarketPool: hasMarketPoolDifferences
					? (marketPool.elements ?? [])
					: notificationCenterStatus.latestMarketPool,
				markAsRead: showSidebar === "notifications",
			});
		},
	});

	const activeModule = useMemo(
		() => modules.top.concat(modules.bottom).find((m) => m.enabled && m.active),
		[modules.bottom, modules.top],
	);
	const firstEnabledModule = useMemo(
		() => modules.top.concat(modules.bottom).find((m) => m.enabled),
		[modules.bottom, modules.top],
	);
	const expandedModule = highlightedModule ?? activeModule ?? firstEnabledModule;

	const renderModules = (mods: AppDrawerModule[]) => {
		return (
			<ForEach collection={mods.filter((m) => m.enabled ?? true)} keyProvider={(module) => module.name}>
				{(module) => (
					<ModuleButton module={module} isActive={activeModule?.name === module.name} onHover={setHighlightedModule} />
				)}
			</ForEach>
		);
	};

	return (
		<>
			<Row
				flexGrow={0}
				className="max-h-dvh sticky top-0 left-0"
				// order -1 keeps the nav rail leftmost even when the Sphere AI chat docks on the left
				// (a left-docked panel also takes order -1 but comes later in source — see SphereAIDockPanel).
				style={{ zIndex: 1000, order: -1 }}
				onMouseLeave={() => setHighlightedModule(null)}
			>
				<StackingContext.Provider value={{ zIndex: 1000 }}>
					<Column
						flexGrow={0}
						className="w-[61px] min-w-[61px] border-r"
						style={{
							borderRightColor: themeCSSVars.palette_N100,
							backgroundColor: themeCSSVars.palette_N700,
						}}
					>
						<MaybeSkeleton className="h-[60px] rounded-none!" value={companyQuery.isLoading ? null : companyQuery.data}>
							{({ submarkLogo }) => (
								<Link
									to="/"
									className="sticky h-[60px] top-0 z-10 flex items-center border-b"
									style={{
										borderBottomColor: themeCSSVars.palette_N100,
										backgroundColor: submarkLogo ? themeCSSVars.palette_N0 : themeCSSVars.palette_P500,
									}}
								>
									<img
										alt="company logo"
										src={submarkLogo ? generateImageObjectUrlFromFileResponse(submarkLogo) : SPHEREIconWhite}
										className="w-full h-9 block object-contain"
									/>
								</Link>
							)}
						</MaybeSkeleton>

						<Column
							minSize="0"
							flexGrow={1}
							gap={8}
							className="overflow-auto p-3"
							style={{
								borderBottomColor: themeCSSVars.palette_N500,
							}}
						>
							{renderModules(modules.top)}
						</Column>
						<Column minSize="0" flexGrow={0} gap={8} className="overflow-auto px-3 pt-3">
							{renderModules(modules.bottom)}
						</Column>
					</Column>
					{expandedModule && (
						<Column
							flexGrow={0}
							flexBasis={0}
							className={{
								"top-0 bottom-0 bg-white border-r h-dvh": true,
								"shadow-xl": !isPinned,
							}}
							style={{
								display: isPinned || highlightedModule ? "flex" : "none",
								position: isPinned ? "relative" : "fixed",
								zIndex: isPinned ? undefined : 500,
								left: isPinned ? undefined : collapsedDrawerWidth,
								borderRightColor: themeCSSVars.palette_N100,
								width: floatingDrawerWidth,
								minWidth: floatingDrawerWidth,
							}}
							onMouseEnter={() => setHighlightedModule(expandedModule)}
						>
							<Row
								flexGrow={0}
								alignItems="center"
								justifyContent="space-between"
								className="h-[60px] border-b px-4"
								style={{
									borderBottomColor: themeCSSVars.palette_N100,
								}}
							>
								<Text type="Body/L/Book" as="div" className="truncate" title={expandedModule.title}>
									{expandedModule.title}
								</Text>
								<IconButton
									icon={isPinned ? "unpin" : "pin"}
									size={16}
									onClick={() => {
										setHighlightedModule(expandedModule);
										togglePin();
									}}
									disabled={!canPin}
									color={themeCSSVars.palette_N500}
									style={{
										visibility: canPin ? "visible" : "hidden",
									}}
								/>
							</Row>
							<Column flexBasis={0} className="overflow-auto pt-3">
								{expandedModule.children}
							</Column>
						</Column>
					)}
				</StackingContext.Provider>
			</Row>

			<StackingContext.Provider value={{ zIndex: 999 }}>
				<AuthorizationGuard requiredService="NOTIFICATION_CENTER">
					{() => (
						<>
							{env.featureFlags.notificationCenter.enabled && (
								<Sidebar
									position={sidebarPosition}
									onChangePosition={setSidebarPosition}
									open={showSidebar === "notifications"}
									onClose={() => setShowSidebar(null)}
									width={440}
								>
									{(forward) => <NotificationCenterSidebarContent {...forward} />}
								</Sidebar>
							)}
						</>
					)}
				</AuthorizationGuard>
			</StackingContext.Provider>

			{devtoolsEnabled && devtoolsPanelOpen && (
				<ReactQueryDevtoolsPanel
					isOpen
					setIsOpen={setDevtoolsPanelOpen}
					position="bottom"
					onDragStart={startDevtoolsResize}
					showCloseButton
					// Fixed positioning is required: the bare panel is `position: relative` and would
					// otherwise render inline inside the drawer instead of docked over the app.
					style={{
						position: "fixed",
						bottom: 0,
						left: 0,
						right: 0,
						height: devtoolsPanelHeight,
						zIndex: 100000,
					}}
				/>
			)}
		</>
	);
}
